import { z } from 'zod';

// Several "boolean" columns (isPreorder, OrderProduct.preorder, …) come back from
// the API as 0/1 integers, others as real booleans depending on the column type.
// Accept either so a row never fails to parse; consumers use truthiness.
const zBoolish = z.union([z.boolean(), z.number()]).nullish();

// ---- Enums (kept as const arrays so lib can build label/color maps and the
// form selects can iterate them). The parsed wire `Order` keeps these fields as
// loose strings so an unexpected server value never fails a `.parse()` — the
// label maps in orders.lib.ts fall back to the raw value. ----

export const ORDER_TYPES = ['facebook', 'glovo', 'online', 'physical', 'phone', 'email'] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_STATUSES = [
    'placed',
    'confirmed',
    'processed',
    'delivered',
    'completed',
    'cancelled',
    'returning',
    'returned',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = [
    'unpaid',
    'partial_payment',
    'paid',
    'payment_failed',
    'refunded',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const DELIVERY_METHODS = [
    'delivery',
    'delivery-locker',
    'personal',
    'personal-brasov',
] as const;
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number];

export const ENTITY_TYPES = ['pf', 'pj'] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

// Cross-border DPD destinations; 'RO' is domestic. Mirrors the API's
// delivery-countries set.
export const DELIVERY_COUNTRIES = ['RO', 'BG', 'HU', 'GR'] as const;
export type DeliveryCountry = (typeof DELIVERY_COUNTRIES)[number];

export const SHIPMENT_PROVIDERS = ['sameday', 'dpd'] as const;
export const PICKUP_POINTS = ['PLOIESTI', 'BRASOV'] as const;
export const REFUND_TYPES = ['cash', 'bank-transfer'] as const;

// ---- Loose reference shapes returned via Sequelize includes ----
const RefSchema = z
    .object({ id: z.number(), name: z.string().nullish() })
    .passthrough();

const PaymentTypeSchema = z
    .object({ id: z.number(), name: z.string().nullish(), handler: z.string().nullish() })
    .passthrough();

const UserRefSchema = z
    .object({ id: z.number(), name: z.string().nullish(), email: z.string().nullish() })
    .passthrough();

const ProductRefSchema = z
    .object({
        id: z.number().nullish(),
        title: z.string().nullish(),
        sku: z.string().nullish(),
        price: z.coerce.number().nullish(),
        thumbnail: z.string().nullish(),
        vatBracket: z.coerce.number().nullish(),
    })
    .passthrough();

const CardRefSchema = z
    .object({
        id: z.number().nullish(),
        name: z.string().nullish(),
        code: z.string().nullish(),
        rarity: z.string().nullish(),
        variantName: z.string().nullish(),
        thumbnail: z.string().nullish(),
        expansion: z.object({ releaseDate: z.string().nullish() }).passthrough().nullish(),
    })
    .passthrough();

export const OrderProductRowSchema = z
    .object({
        productId: z.number().nullish(),
        quantity: z.coerce.number().nullish(),
        quantityReturned: z.coerce.number().nullish(),
        unitPrice: z.coerce.number().nullish(),
        vatRate: z.coerce.number().nullish(),
        preorder: zBoolish,
        backorder: zBoolish,
        thumbnail: z.string().nullish(),
        stockAccountId: z.number().nullish(),
        product: ProductRefSchema.nullish(),
    })
    .passthrough();

export const OrderCardRowSchema = z
    .object({
        cardId: z.number().nullish(),
        quantity: z.coerce.number().nullish(),
        quantityReturned: z.coerce.number().nullish(),
        unitPrice: z.coerce.number().nullish(),
        stockAccountId: z.number().nullish(),
        card: CardRefSchema.nullish(),
    })
    .passthrough();

export const OrderVoucherRowSchema = z
    .object({
        value: z.coerce.number().nullish(),
        voucher: z.object({ name: z.string().nullish(), code: z.string().nullish() }).passthrough().nullish(),
    })
    .passthrough();

export const InvoiceRowSchema = z
    .object({
        id: z.number(),
        type: z.string().nullish(),
        series: z.string().nullish(),
        number: z.union([z.string(), z.number()]).nullish(),
        date: z.string().nullish(),
        cancels: z.union([z.number(), z.boolean(), z.null()]).optional(),
    })
    .passthrough();

export const ActivityRowSchema = z
    .object({
        id: z.number().nullish(),
        activityType: z.string().nullish(),
        // The API stores this as a JSON string; lib normalizes it to an object.
        activityData: z.unknown().nullish(),
        createdAt: z.string().nullish(),
    })
    .passthrough();

export const HistoryRowSchema = z
    .object({
        status: z.string().nullish(),
        statusLabel: z.string().nullish(),
        statusState: z.string().nullish(),
        statusDate: z.string().nullish(),
        reason: z.string().nullish(),
        county: z.string().nullish(),
        transitLocation: z.string().nullish(),
    })
    .passthrough();

export const ReviewSchema = z
    .object({
        rating: z.coerce.number().nullish(),
        comment: z.string().nullish(),
        createdAt: z.string().nullish(),
    })
    .passthrough();

export const AttributionSchema = z
    .object({
        gclid: z.string().nullish(),
        fbclid: z.string().nullish(),
        fbc: z.string().nullish(),
        fbp: z.string().nullish(),
        gaClientId: z.string().nullish(),
        clientIp: z.string().nullish(),
        clientUserAgent: z.string().nullish(),
        conversionSentAt: z.string().nullish(),
    })
    .passthrough();

export const InventoryEntrySchema = z
    .object({
        productId: z.number().nullish(),
        cardId: z.number().nullish(),
        stockAccountId: z.number().nullish(),
    })
    .passthrough();

// ---- The order. One schema covers both the list payload (fewer includes) and
// the detail payload — association arrays are optional, scalar extras nullish. ----
export const OrderSchema = z
    .object({
        id: z.number(),
        locationId: z.number().nullish(),
        orderNumber: z.string().nullish(),
        orderType: z.string().nullish(),
        email: z.string().nullish(),
        status: z.string().nullish(),
        paymentStatus: z.string().nullish(),
        deliveryStatus: z.string().nullish(),
        isPreorder: zBoolish,
        isBackorder: zBoolish,
        isSingles: zBoolish,
        paymentTypeId: z.number().nullish(),

        billingName: z.string().nullish(),
        billingAddress: z.string().nullish(),
        billingAddress2: z.string().nullish(),
        billingPhoneNumber: z.string().nullish(),
        billingCountry: z.string().nullish(),
        billingCityName: z.string().nullish(),
        billingPostCode: z.string().nullish(),

        delivery: z.string().nullish(),
        deliveryName: z.string().nullish(),
        deliveryAddress: z.string().nullish(),
        deliveryAddress2: z.string().nullish(),
        deliveryPhoneNumber: z.string().nullish(),
        deliveryCountry: z.string().nullish(),
        deliveryCityName: z.string().nullish(),
        deliveryPostCode: z.string().nullish(),
        deliveryLockerId: z.union([z.string(), z.number()]).nullish(),
        deliveryLockerName: z.string().nullish(),
        deliveryReference: z.string().nullish(),
        deliveryProvider: z.string().nullish(),

        entityType: z.string().nullish(),
        companyName: z.string().nullish(),
        vatNumber: z.string().nullish(),
        registryNumber: z.string().nullish(),

        total: z.coerce.number().nullish(),
        vat: z.coerce.number().nullish(),
        grandTotal: z.coerce.number().nullish(),
        shippingCost: z.coerce.number().nullish(),
        realShippingCost: z.coerce.number().nullish(),
        voucherValue: z.coerce.number().nullish(),
        bankPaymentReference: z.string().nullish(),
        cardPaymentReference: z.string().nullish(),

        comment: z.string().nullish(),
        // Who made the POS sale — one or more employee display names, '; '-joined.
        saleAttribution: z.string().nullish(),
        issues: z.coerce.number().nullish(),
        oldestIssueAt: z.string().nullish(),
        emailOrderCount: z.coerce.number().nullish(),
        documentId: z.union([z.string(), z.number()]).nullish(),
        referenceId: z.string().nullish(),
        userId: z.number().nullish(),

        // JSON columns — server may hand them back as a string or already parsed.
        extraData: z.unknown().nullish(),
        refundData: z.unknown().nullish(),

        createdAt: z.string().nullish(),
        updatedAt: z.string().nullish(),

        // Associations
        paymentType: PaymentTypeSchema.nullish(),
        user: UserRefSchema.nullish(),
        billingCity: RefSchema.nullish(),
        billingCounty: RefSchema.nullish(),
        billingLocation: RefSchema.nullish(),
        deliveryCity: RefSchema.nullish(),
        deliveryCounty: RefSchema.nullish(),
        deliveryLocation: RefSchema.nullish(),
        products: z.array(OrderProductRowSchema).optional().default([]),
        cards: z.array(OrderCardRowSchema).optional().default([]),
        vouchers: z.array(OrderVoucherRowSchema).optional().default([]),
        invoices: z.array(InvoiceRowSchema).optional().default([]),
        activity: z.array(ActivityRowSchema).optional().default([]),
        history: z.array(HistoryRowSchema).optional().default([]),
        review: ReviewSchema.nullish(),
        attribution: AttributionSchema.nullish(),
        inventoryEntries: z.array(InventoryEntrySchema).optional().default([]),
    })
    .passthrough();

export type Order = z.infer<typeof OrderSchema>;

export const OrderListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(OrderSchema),
    count: z.number(),
});

export const OrderResponseSchema = z.object({
    success: z.boolean(),
    data: OrderSchema,
});

// ---- Shipments (v2 module) ----
export const ShipmentSchema = z
    .object({
        id: z.number(),
        orderId: z.number().nullish(),
        provider: z.string().nullish(),
        type: z.string().nullish(), // main | extra | exchange
        awbNumber: z.string().nullish(),
        shipmentId: z.union([z.string(), z.number()]).nullish(),
        cod: z.coerce.number().nullish(),
        cost: z.coerce.number().nullish(),
        deliveryStatus: z.string().nullish(),
        cancelledAt: z.string().nullish(),
        createdAt: z.string().nullish(),
    })
    .passthrough();

export type Shipment = z.infer<typeof ShipmentSchema>;

export const ShipmentListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(ShipmentSchema),
    count: z.number().optional(),
});

export const ShipmentResponseSchema = z.object({
    success: z.boolean(),
    data: ShipmentSchema,
});

export const EnabledProviderSchema = z
    .object({ id: z.string(), label: z.string().nullish() })
    .passthrough();
export const EnabledProvidersResponseSchema = z.union([
    z.object({ success: z.boolean(), data: z.array(EnabledProviderSchema) }),
    z.array(EnabledProviderSchema),
]);

// ---- Normalized line item (computed in lib from products[]/cards[]). Plain TS
// type, not a wire schema — it is the shape the view/form render against. ----
export type OrderItem = {
    type: 'product' | 'card';
    id: number;
    label: string;
    secondary: string | null; // sku / code+variant
    price: number;
    quantity: number;
    quantityReturned: number;
    thumbnail: string | null;
    preorder: boolean;
    backorder: boolean;
    vatRate: number | null;
    stockAccountId: number | null;
};

// ---- Create / edit form ----
export const OrderFormItemSchema = z.object({
    type: z.enum(['product', 'card']),
    id: z.number(),
    label: z.string().default(''),
    secondary: z.string().nullish(),
    price: z.coerce.number().min(0),
    quantity: z.coerce.number().int().min(1),
    quantityReturned: z.coerce.number().int().min(0).default(0),
    thumbnail: z.string().nullish(),
    preorder: z.boolean().default(false),
    backorder: z.boolean().default(false),
    vatRate: z.coerce.number().nullish(),
});
export type OrderFormItem = z.infer<typeof OrderFormItemSchema>;

// A chosen county/city/location reference inside the form (RO addresses).
const PickSchema = z.object({ id: z.number(), name: z.string().default('') }).nullable();

export const OrderFormSchema = z.object({
    orderType: z.enum(ORDER_TYPES).default('online'),
    paymentTypeId: z.coerce.number().nullable().default(null),
    email: z.string().min(1, 'Email obligatoriu'),
    entityType: z.enum(ENTITY_TYPES).default('pf'),
    companyName: z.string().optional().or(z.literal('')),
    vatNumber: z.string().optional().or(z.literal('')),
    registryNumber: z.string().optional().or(z.literal('')),

    delivery: z.enum(DELIVERY_METHODS).default('delivery'),

    // Billing
    billingName: z.string().optional().or(z.literal('')),
    billingAddress: z.string().optional().or(z.literal('')),
    billingAddress2: z.string().optional().or(z.literal('')),
    billingPhoneNumber: z.string().optional().or(z.literal('')),
    billingCountry: z.enum(DELIVERY_COUNTRIES).default('RO'),
    billingCounty: PickSchema.default(null),
    billingCity: PickSchema.default(null),
    billingLocation: PickSchema.default(null),
    billingCityName: z.string().optional().or(z.literal('')),
    billingPostCode: z.string().optional().or(z.literal('')),

    // Delivery
    deliveryName: z.string().optional().or(z.literal('')),
    deliveryAddress: z.string().optional().or(z.literal('')),
    deliveryAddress2: z.string().optional().or(z.literal('')),
    deliveryPhoneNumber: z.string().optional().or(z.literal('')),
    deliveryCountry: z.enum(DELIVERY_COUNTRIES).default('RO'),
    deliveryCounty: PickSchema.default(null),
    deliveryCity: PickSchema.default(null),
    deliveryLocation: PickSchema.default(null),
    deliveryCityName: z.string().optional().or(z.literal('')),
    deliveryPostCode: z.string().optional().or(z.literal('')),
    deliveryLockerId: z.string().optional().or(z.literal('')),
    deliveryLockerName: z.string().optional().or(z.literal('')),

    items: z.array(OrderFormItemSchema).default([]),
    shippingCost: z.coerce.number().min(0).default(0),
    comment: z.string().optional().or(z.literal('')),

    // Carried through on edit so the serializer can preserve them.
    id: z.number().nullish(),
    orderNumber: z.string().nullish(),
    status: z.string().nullish(),
    paymentStatus: z.string().nullish(),
    preorderId: z.number().nullish(),
});
export type OrderForm = z.infer<typeof OrderFormSchema>;

// ---- Status-transition modal payloads (sent as `extraData` to status-update) ----
export const ProcessPackageSchema = z.object({
    weight: z.coerce.number().min(0).default(1),
    length: z.coerce.number().min(0).default(0),
    width: z.coerce.number().min(0).default(0),
    height: z.coerce.number().min(0).default(0),
});
export type ProcessPackage = z.infer<typeof ProcessPackageSchema>;

export const ProcessFormSchema = z.object({
    provider: z.enum(SHIPMENT_PROVIDERS).default('sameday'),
    pickupPoint: z.enum(PICKUP_POINTS).default('PLOIESTI'),
    personalDropoff: z.boolean().default(false),
    emailNotifications: z.boolean().default(true),
    smsNotifications: z.boolean().default(true),
    packages: z.array(ProcessPackageSchema).min(1).default([{ weight: 1, length: 0, width: 0, height: 0 }]),
});
export type ProcessForm = z.infer<typeof ProcessFormSchema>;

export const ReturnProductSchema = z.object({
    type: z.enum(['product', 'card']),
    id: z.number(),
    label: z.string().default(''),
    maxQuantity: z.coerce.number().int().min(0).default(0),
    quantity: z.coerce.number().int().min(0).default(0),
});

// Payload keys mirror what the API's Order.return reads from extraData:
// refundType / refundName (account holder) / refundAccount (IBAN) /
// returnShipping (numeric transport-retur cost) / products[{id,quantity}].
// refundValue is computed server-side (grandTotal delta), so it is NOT sent.
export const ReturnFormSchema = z.object({
    refundType: z.enum(REFUND_TYPES).default('cash'),
    refundName: z.string().optional().or(z.literal('')),
    refundAccount: z.string().optional().or(z.literal('')),
    returnShipping: z.coerce.number().min(0).default(0),
    products: z.array(ReturnProductSchema).default([]),
});
export type ReturnForm = z.infer<typeof ReturnFormSchema>;

export const CompleteFormSchema = z.object({
    sendEmail: z.boolean().default(true),
});
export type CompleteForm = z.infer<typeof CompleteFormSchema>;

// ---- Shipment create form (extra / exchange) ----
export const ShipmentFormSchema = z.object({
    pickupPoint: z.enum(PICKUP_POINTS).default('PLOIESTI'),
    cod: z.coerce.number().min(0).default(0),
    packages: z.array(ProcessPackageSchema).min(1).default([{ weight: 1, length: 0, width: 0, height: 0 }]),
});
export type ShipmentForm = z.infer<typeof ShipmentFormSchema>;

// ---- Order split (POST /v2/orders/:id/split) ----
export const SplitInvoicingSchema = z.object({
    stornoed: z
        .array(
            z
                .object({
                    invoiceId: z.number(),
                    type: z.string().nullish(),
                    series: z.string().nullish(),
                    number: z.union([z.string(), z.number()]).nullish(),
                    stornoId: z.number().nullish(),
                })
                .passthrough(),
        )
        .default([]),
    voided: z.array(z.object({ invoiceId: z.number() }).passthrough()).default([]),
    created: z
        .array(z.object({ orderId: z.number(), invoiceId: z.number(), type: z.string() }).passthrough())
        .default([]),
});

export const SplitOrderResultSchema = z.object({
    sourceOrder: OrderSchema,
    newOrder: OrderSchema,
    invoicing: SplitInvoicingSchema,
    invoicingError: z.string().nullish(),
});
export type SplitOrderResult = z.infer<typeof SplitOrderResultSchema>;

export const SplitOrderResponseSchema = z.object({
    success: z.boolean(),
    data: SplitOrderResultSchema,
});

export type SplitOrderPayload = {
    products: { id: number; quantity: number }[];
    cards: { id: number; quantity: number }[];
    shippingTarget: 'source' | 'new';
    comment?: string;
};
